import type { InjunctionType } from "@/api/person/fristForhaandsvisning";
import { useFristForhaandsvisning } from "@/hooks/person-hooks/useFristForhaandsvisning";

import { beregnFristForhaandsvisningTekst } from "./fristForhaandsvisningTekst";
import { formatDate } from "./utils/formatters";

interface FristForhaandsvisningProps {
  type: InjunctionType;
  personId: string;
  /** Kendelses- eller afgørelsesdatoen som yyyy-MM-dd, eller null hvis den ikke er valgt. */
  kendelseDato: string | null;
  periodeLaengdeMaaneder: number | null;
}

/**
 * Viser hvilke datoer sagen vil få, før den oprettes eller gemmes.
 *
 * Datoerne kommer fra backenden, ikke fra en beregning her: fristen afhænger af advokatens
 * udøvende perioder, og en kopi af reglen klientside ville være uenig med backenden netop
 * i bero-tilfældene.
 */
const FristForhaandsvisning = ({
  type,
  personId,
  kendelseDato,
  periodeLaengdeMaaneder,
}: FristForhaandsvisningProps) => {
  const { data, isError, isLoading } = useFristForhaandsvisning({
    type,
    personId,
    kendelseDato: kendelseDato ?? undefined,
    periodeLaengdeMaaneder: periodeLaengdeMaaneder ?? undefined,
  });

  const harValgt = Boolean(kendelseDato && periodeLaengdeMaaneder);

  if (!harValgt) {
    return (
      <div className="border rounded p-3 bg-light">
        <p className="text-uppercase small fw-bold text-muted mb-0">
          Beregnet af systemet
        </p>
        <p className="text-muted small mb-0 mt-1">
          Vælg dato og længde for at se indfrielsesperioden og fristen.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="border rounded p-3 bg-light placeholder-glow">
        <span className="visually-hidden">Beregner frist</span>
        <span className="placeholder col-6 rounded d-block" />
      </div>
    );
  }

  // Forhåndsvisningen er ren information. Kan den ikke hentes, skal sagen fortsat kunne
  // oprettes — backenden beregner autoritativt ved gem.
  if (isError || !data) {
    return (
      <div className="border rounded p-3 bg-light">
        <p className="text-uppercase small fw-bold text-muted mb-0">
          Beregnet af systemet
        </p>
        <p className="text-muted small mb-0 mt-1">
          Fristen kan ikke beregnes lige nu. Den beregnes uanset hvad, når sagen
          gemmes.
        </p>
      </div>
    );
  }

  const tekst = beregnFristForhaandsvisningTekst(data, formatDate);
  const maerkatKlasse =
    tekst.maerkat === "loebende" ? "text-bg-secondary" : "text-bg-warning";

  return (
    <div className="border rounded p-3 bg-light">
      <div className="d-flex justify-content-between align-items-center gap-2">
        <p className="text-uppercase small fw-bold text-muted mb-0">
          Beregnet af systemet
        </p>
        <span className={`badge ${maerkatKlasse}`}>{tekst.maerkatTekst}</span>
      </div>

      {tekst.forklaring && (
        <p className="small mb-0 mt-2">{tekst.forklaring}</p>
      )}

      <dl className="row mb-0 mt-2 small">
        <dt className="col-7 fw-normal text-muted">Indfrielsesperiode</dt>
        <dd className="col-5 mb-1 text-end fw-semibold">{tekst.periode}</dd>
        <dt className="col-7 fw-normal text-muted">Frist for dokumentation</dt>
        <dd className="col-5 mb-0 text-end fw-semibold">{tekst.frist}</dd>
      </dl>
    </div>
  );
};

export default FristForhaandsvisning;
